import { ListOrderedIcon, ListIcon } from "lucide-react";
import { useEffect, useState } from "react";

import { checkState } from "@/helpers/editor/check-state";
import { runEditorCommand } from "@/helpers/editor/command-style";

import { cn } from "@/lib/utils";

import ToolbarButton from "./toolbar-button";

const ListOrder: React.FC<
  React.PropsWithChildren<
    React.ComponentProps<"button"> & {
      editorRef: React.RefObject<HTMLDivElement>;
    }
  >
> = ({ className, editorRef, ...props }) => {
  const [isOrderedList, setIsOrderedList] = useState<boolean>(false);
  const [isUnorderedList, setIsUnorderedList] = useState<boolean>(false);

  const handleCommand = (command: string) => {
    runEditorCommand(editorRef, command);
  };

  useEffect(() => {
    const handleSelectionChange = () => {
      setIsOrderedList(!!checkState("insertOrderedList", editorRef));
      setIsUnorderedList(!!checkState("insertUnorderedList", editorRef));
    };

    document.addEventListener("selectionchange", handleSelectionChange);

    return () => {
      document.removeEventListener("selectionchange", handleSelectionChange);
    };
  }, [editorRef]);

  // Tab / Shift + Tab برای nested list
  useEffect(() => {
    const editor = editorRef.current;

    if (!editor) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key !== "Tab") return;

      const selection = window.getSelection();

      if (!selection || selection.rangeCount === 0) return;

      let node = selection.anchorNode as HTMLElement | null;

      if (node?.nodeType === Node.TEXT_NODE) {
        node = node.parentElement;
      }

      const li = node?.closest("li");

      if (!li) return;

      e.preventDefault();

      if (e.shiftKey) {
        document.execCommand("outdent");
      } else {
        document.execCommand("indent");
      }

      // اجبار به refresh شدن UI
      setTimeout(() => {
        const lists = editor.querySelectorAll("ul, ol");

        lists.forEach((list) => {
          if (list instanceof HTMLElement) {
            const level =
              list.closest("li")?.querySelectorAll("ul, ol").length ?? 0;

            list.style.paddingInlineStart = `${24 + level * 24}px`;
          }
        });
      }, 0);
    };

    editor.addEventListener("keydown", handleKeyDown);

    return () => {
      editor.removeEventListener("keydown", handleKeyDown);
    };
  }, [editorRef]);

  return (
    <>
      <ToolbarButton
        className={cn(
          className,
          isOrderedList ? "bg-primary/10" : "bg-transparent"
        )}
        onClick={() => handleCommand("insertOrderedList")}
        {...props}
      >
        <ListOrderedIcon />
      </ToolbarButton>

      <ToolbarButton
        className={cn(
          className,
          isUnorderedList ? "bg-primary/10" : "bg-transparent"
        )}
        onClick={() => handleCommand("insertUnorderedList")}
        {...props}
      >
        <ListIcon />
      </ToolbarButton>
    </>
  );
};

export default ListOrder;
